import React, { useState, useEffect, useContext } from "react";
import classNames from "classnames/bind";
import styles from "./SheetManagement.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faEdit, faTrash, faExternalLinkAlt, faStar, faCheckCircle, faLock, faUnlock } from "@fortawesome/free-solid-svg-icons";
import { ToastContext } from "~/components/context/ToastContext";
import { ModalContext } from "~/components/context/ModalContext";
import { BASE_API_URL } from "~/helper/apiService";
import Skeleton from "~/components/Skeleton/Skeleton";

const cx = classNames.bind(styles);

function SheetManagement() {
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSheetId, setActiveSheetId] = useState(null);
  const [defaultSheetId, setDefaultSheetId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingSheet, setEditingSheet] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const { showToast } = useContext(ToastContext);
  const { showModal: showConfirmModal } = useContext(ModalContext);

  const fetchSheets = async () => {
    try {
      setLoading(true);
      const [sheetsResponse, activeResponse] = await Promise.all([
        fetch(`${BASE_API_URL}api/sheets`, { credentials: "include" }),
        fetch(`${BASE_API_URL}api/sheets/config/active`, { credentials: "include" })
      ]);
      
      const sheetsData = await sheetsResponse.json();
      const activeData = await activeResponse.json();
      
      if (sheetsResponse.ok && sheetsData.success) {
        setSheets(sheetsData.sheets || []);
        // Tìm sheet có is_default = 1
        const defaultSheet = sheetsData.sheets?.find(s => s.is_default === 1);
        if (defaultSheet) {
          setDefaultSheetId(defaultSheet.id);
        }
      } else {
        showToast(sheetsData.error || "Lỗi khi lấy danh sách sheets", "error");
      }
      
      if (activeResponse.ok && activeData.success && activeData.activeSheet) {
        setActiveSheetId(activeData.activeSheet.id);
      }
    } catch (error) {
      showToast("Lỗi kết nối: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSheets();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAdd = () => {
    setEditingSheet(null);
    setFormData({
      name: "",
    });
    setShowModal(true);
  };

  const handleEdit = (sheet) => {
    setEditingSheet(sheet);
    setFormData({
      name: sheet.name,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      showToast("Vui lòng nhập tên sheet", "error");
      return;
    }

    setSubmitting(true);
    try {
      const url = editingSheet
        ? `${BASE_API_URL}api/sheets/${editingSheet.id}`
        : `${BASE_API_URL}api/sheets`;
      
      const method = editingSheet ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        showToast(
          editingSheet ? "Cập nhật sheet thành công" : "Tạo sheet thành công",
          "success"
        );
        setShowModal(false);
        fetchSheets();
      } else {
        // Hiển thị lỗi chi tiết
        let errorMsg = data.error || "Có lỗi xảy ra";
        showToast(errorMsg, "error");
        
        // Nếu cần bật Drive API, hiển thị thông báo chi tiết
        if (data.needsDriveAPI) {
          setTimeout(() => {
            alert(
              "⚠️ Lỗi:\n\n" +
              errorMsg +
              "\n\n" +
              "Sheet chưa được tạo. Vui lòng bật Google Drive API và thử lại."
            );
          }, 500);
        }
      }
    } catch (error) {
      showToast("Lỗi kết nối: " + error.message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSetActive = async (sheet) => {
    try {
      const response = await fetch(`${BASE_API_URL}api/sheets/config/active/${sheet.id}`, {
        method: "PUT",
        credentials: "include",
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        showToast(`Đã đặt "${sheet.name}" làm sheet đang sử dụng`, "success");
        setActiveSheetId(sheet.id);
        fetchSheets(); // Reload để cập nhật is_active
      } else {
        showToast(data.error || "Có lỗi xảy ra", "error");
      }
    } catch (error) {
      showToast("Lỗi kết nối: " + error.message, "error");
    }
  };

  const handleSetDefault = async (sheet) => {
    try {
      const response = await fetch(`${BASE_API_URL}api/sheets/default/${sheet.id}`, {
        method: "PUT",
        credentials: "include",
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        showToast(`Đã đặt "${sheet.name}" làm tháng mặc định`, "success");
        setDefaultSheetId(sheet.id);
        fetchSheets(); // Reload để cập nhật is_default
      } else {
        showToast(data.error || "Có lỗi xảy ra", "error");
      }
    } catch (error) {
      showToast("Lỗi kết nối: " + error.message, "error");
    }
  };

  const handleToggleLock = async (sheet) => {
    const newLockStatus = !sheet.is_locked;
    try {
      const response = await fetch(`${BASE_API_URL}api/sheets/lock/${sheet.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ is_locked: newLockStatus }),
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        showToast(
          newLockStatus 
            ? `Đã khóa chỉnh sửa tháng "${sheet.name}"` 
            : `Đã mở khóa chỉnh sửa tháng "${sheet.name}"`,
          "success"
        );
        fetchSheets(); // Reload để cập nhật is_locked
      } else {
        showToast(data.error || "Có lỗi xảy ra", "error");
      }
    } catch (error) {
      showToast("Lỗi kết nối: " + error.message, "error");
    }
  };

  const handleDelete = (sheet) => {
    showConfirmModal({
      title: "Xác nhận xóa",
      message: `Bạn có chắc chắn muốn xóa sheet "${sheet.name}"?`,
      onConfirm: async () => {
        try {
          const response = await fetch(`${BASE_API_URL}api/sheets/${sheet.id}`, {
            method: "DELETE",
            credentials: "include",
          });

          const data = await response.json();
          
          if (response.ok && data.success) {
            showToast("Xóa sheet thành công", "success");
            fetchSheets();
          } else {
            showToast(data.error || "Có lỗi xảy ra", "error");
          }
        } catch (error) {
          showToast("Lỗi kết nối: " + error.message, "error");
        }
      },
      onCancel: null,
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleString("vi-VN", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return <Skeleton />;
  }

  return (
    <div className={cx("container")}>
      <div className={cx("header")}>
        <h2>Quản lý Sheet</h2>
        <button className={cx("add-button")} onClick={handleAdd}>
          <FontAwesomeIcon icon={faPlus} />
          Tạo Sheet mới
        </button>
      </div>

      <div className={cx("table-container")}>
        <table className={cx("table")}>
          <thead>
            <tr>
              <th>ID</th>
              <th>Tên Sheet</th>
              <th>Ngày tạo</th>
              <th>Trạng thái</th>
              <th>Mặc định</th>
              <th>Khóa</th>
              <th>Thao tác</th>
            </tr>
          </thead>
          <tbody>
            {sheets.length === 0 ? (
              <tr>
                <td colSpan="7" className={cx("empty")}>
                  Chưa có sheet nào. Hãy tạo sheet mới!
                </td>
              </tr>
            ) : (
              sheets.map((sheet) => (
                <tr key={sheet.id}>
                  <td>{sheet.id}</td>
                  <td>{sheet.name}</td>
                  <td>{formatDate(sheet.created_at)}</td>
                  <td>
                    {activeSheetId === sheet.id ? (
                      <span className={cx("active-badge")}>
                        <FontAwesomeIcon icon={faCheckCircle} /> Đang sử dụng
                      </span>
                    ) : (
                      <button
                        className={cx("set-active-button")}
                        onClick={() => handleSetActive(sheet)}
                        title="Đặt làm sheet đang sử dụng"
                      >
                        <FontAwesomeIcon icon={faStar} /> Đặt làm active
                      </button>
                    )}
                  </td>
                  <td>
                    {defaultSheetId === sheet.id ? (
                      <span className={cx("active-badge")}>
                        <FontAwesomeIcon icon={faStar} /> Mặc định
                      </span>
                    ) : (
                      <button
                        className={cx("set-active-button")}
                        onClick={() => handleSetDefault(sheet)}
                        title="Đặt làm tháng mặc định (khi load trang sẽ hiển thị tháng này)"
                      >
                        <FontAwesomeIcon icon={faStar} /> Đặt mặc định
                      </button>
                    )}
                  </td>
                  <td>
                    <button
                      className={cx(sheet.is_locked ? "lock-button" : "unlock-button")}
                      onClick={() => handleToggleLock(sheet)}
                      title={sheet.is_locked ? "Mở khóa chỉnh sửa" : "Khóa chỉnh sửa"}
                    >
                      <FontAwesomeIcon icon={sheet.is_locked ? faLock : faUnlock} />
                      {sheet.is_locked ? " Đã khóa" : " Mở khóa"}
                    </button>
                  </td>
                  <td>
                    <div className={cx("actions")}>
                      <button
                        className={cx("edit-button")}
                        onClick={() => handleEdit(sheet)}
                        title="Sửa"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button
                        className={cx("delete-button")}
                        onClick={() => handleDelete(sheet)}
                        title="Xóa"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className={cx("modal-overlay")} onClick={() => setShowModal(false)}>
          <div className={cx("modal")} onClick={(e) => e.stopPropagation()}>
            <div className={cx("modal-header")}>
              <h3>{editingSheet ? "Sửa Sheet" : "Tạo Sheet mới"}</h3>
              <button
                className={cx("close-button")}
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit} className={cx("form")}>
              <div className={cx("form-group")}>
                <label htmlFor="name">Tên Sheet *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Nhập tên sheet"
                  required
                  maxLength={100}
                />
                {editingSheet && (
                  <p className={cx("info-text")}>
                    Lưu ý: Chỉ cập nhật tên trong database. Google Sheet sẽ không được đổi tên.
                  </p>
                )}
                {!editingSheet && (
                  <p className={cx("info-text")}>
                    Một Google Sheet mới sẽ được tạo với tên này.
                  </p>
                )}
              </div>
              <div className={cx("form-actions")}>
                <button
                  type="button"
                  className={cx("cancel-button")}
                  onClick={() => setShowModal(false)}
                  disabled={submitting}
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  className={cx("submit-button")}
                  disabled={submitting}
                >
                  {submitting ? "Đang xử lý..." : editingSheet ? "Cập nhật" : "Tạo Sheet"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default SheetManagement;
